/**
 * Methods related to screen calculations
 */
import _toNumber from "lodash/toNumber";
import _includes from "lodash/includes";
import { getLastArticleHeight } from "./dom";
import { detect } from "detect-browser";

/**
 * Return the current screen width in pixels
 * @returns {number}
 */
export const getScreenWidth = () => {
    return document.documentElement.clientWidth || 0;
};

/**
 * Return the current screen height in pixels
 * @returns {number}
 */
export const getScreenHeight = () => {
    return document.documentElement.clientHeight || 0;
};

/**
 * Return the amount of whitespace in px we need to add in order to fill the screen
 * @returns {number}
 */
export const getSpaceToFillBottom = () => {
    const lastArticleHeight = getLastArticleHeight();
    const height = getScreenHeight();
    const topHeaderOffset = getTopHeaderOffset();
    const difference = height - topHeaderOffset - lastArticleHeight;
    if (difference > 0) {
        return difference;
    } else {
        return 0; // Article tall enough
    }
};

/**
 * Are we showing the mobile version or the desktop version (based on pixels)
 * @returns {boolean}
 */
export const isMobile = () => {
    return getScreenWidth() <= getMobileBreakpoint();
};

/**
 * Return the width at which we switch to a mobile version (in pixels)
 * @returns {number}
 */
export const getMobileBreakpoint = () => {
    return 768;
};
/**
 * Return the top nav bar height
 *
 */
export const getTopHeaderOffset = () => {
    const offsetElement = document.querySelector(".top-nav-bar");
    if (offsetElement) {
        const height = getComputedStyle(offsetElement).height.replace(
            /[^\d]*$/,
            ""
        );
        const padding = 48;
        const heightValue = _toNumber(height) + padding;

        return heightValue;
    } else {
        return 0;
    }
};

/**
 * Return a map containing valid combinations of browsers
 * @returns {Object} map
 */
export const getValidBrowserCombosMap = () => {
    // Combinations from https://github.com/DamonOehlman/detect-browser/blob/master/test/logic.js
    return {
        "chrome": {
            // Chrome on Windows and MacOs (Android and iOS are here just in case, but should look different - see below)
            validOs: [
                "Windows 10",
                "Windows 7",
                "Windows 8.1",
                "Mac OS",
                "Android OS",
                "iOS",
            ],
        },
        "chromium-webview": {
            // Android Chromium Webview
            validOs: ["Android OS"],
        },
        "ie": {
            // Internet Explorer for PC
            validOs: ["Windows 10", "Windows 8.1", "Windows 7"],
            validVersions: ["11.0.0"],
        },
        "ios": {
            // Safari for iOS
            validOs: ["iOS"],
        },
        "crios": {
            // Chrome for iOS
            validOs: ["iOS"],
        },
        "edge": {
            // Edge for PC
            validOs: ["Windows 10", "Windows 8.1"],
        },
        "edge-chromium": {
            // Edge Chromium (ie "edg") for PC
            validOs: ["Windows 10", "Windows 8.1"],
        },
    };
};

/**
 * Is the current browser valid
 * @returns {boolean}
 */
export const isValidBrowser = () => {
    const { name, os, version } = detect() || {};
    console.log(name, os, version);
    const map = getValidBrowserCombosMap();
    let isValid = false; // Assume browser is invalid if we cannot get data

    if (name && os) {
        const browserValues = map[name];

        if (browserValues) {
            const { validOs, validVersions } = browserValues;
            if (_includes(validOs, os)) {
                // If there is a list of valid versions and the version we have is not included
                if (version && validVersions) {
                    if (!_includes(validVersions, version)) {
                        console.log("Valid browser and OS, invalid version");
                        isValid = false;
                    } else {
                        console.log("Valid browser, OS, and version");
                        isValid = true;
                    }
                } else {
                    // No version restriction or info
                    console.log("Valid browser and OS");
                    isValid = true;
                }
            } else {
                console.log("Valid browser but invalid OS");
                isValid = false;
            }
        } else {
            // Not one of the available keys in the map, not a valid browser
            console.log("Invalid browser");
            isValid = false;
        }
    } else {
        console.log("Could not get browser values");
        isValid = false;
    }

    return isValid;
};
